import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import { EventAvailableOutlined } from '@mui/icons-material';
import { Company, SubscriptionStatus, SubscriptionStatusDto, UpdateSubscriptionRequest } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface SubscriptionDialogProps {
  open: boolean;
  company: Company | null;
  subscription: SubscriptionStatusDto | null;
  loading: boolean;
  saving: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (request: UpdateSubscriptionRequest) => Promise<void>;
}

const toLocalInput = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIso = (value: string): string => new Date(value).toISOString();

const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({
  open, company, subscription, loading, saving, error, onClose, onSubmit,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [planCode, setPlanCode] = useState('MANUAL');
  const [status, setStatus] = useState<SubscriptionStatus>(SubscriptionStatus.ACTIVE);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [notes, setNotes] = useState('');
  const [paidActivation, setPaidActivation] = useState(false);
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPlanCode(subscription?.planCode || 'MANUAL');
    setStatus(subscription?.status || SubscriptionStatus.ACTIVE);
    setStartsAt(toLocalInput(subscription?.startsAt) || toLocalInput(new Date().toISOString()));
    setEndsAt(toLocalInput(subscription?.endsAt));
    setNotes(subscription?.notes || '');
    setPaidActivation(false);
    setDateError('');
  }, [open, subscription]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (endsAt && endsAt < startsAt) { setDateError(t('subscription.invalidDateRange')); return; }
    setDateError('');
    await onSubmit({
      planCode: planCode.trim(),
      status,
      startsAt: toIso(startsAt),
      endsAt: endsAt ? toIso(endsAt) : undefined,
      notes: notes.trim() || undefined,
      paidActivation,
    });
  };

  return (
    <AppDialog presentation="form" open={open} onClose={onClose} closeDisabled={saving} fullWidth maxWidth="sm" fullScreen={mobile} PaperProps={{ sx: { borderRadius: mobile ? 0 : 2, overflow: 'hidden' } }}>
      <form onSubmit={handleSubmit}>
        <AppDialogHeader icon={<EventAvailableOutlined />} subtitle={company?.name}>{t('subscription.manageTitle')}</AppDialogHeader>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {company?.name && <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{company.name}</Typography>}
            {error && <Alert severity="error">{error}</Alert>}
            {dateError && <Alert severity="warning">{dateError}</Alert>}
            <Typography variant="body2" color="text.secondary">{t('subscription.formHint')}</Typography>
            <FormControlLabel
              control={<Checkbox checked={paidActivation} onChange={(event) => setPaidActivation(event.target.checked)} />}
              label={t('subscription.paidActivation')}
              disabled={loading || saving}
            />
            <TextField
              label={t('subscription.planCode')}
              value={planCode}
              onChange={(event) => setPlanCode(event.target.value)}
              required
              disabled={loading || saving}
            />
            <FormControl fullWidth disabled={loading || saving}>
              <InputLabel>{t('subscription.status')}</InputLabel>
              <Select value={status} label={t('subscription.status')} onChange={(event) => setStatus(event.target.value as SubscriptionStatus)}>
                <MenuItem value={SubscriptionStatus.ACTIVE}>{t('subscription.active')}</MenuItem>
                <MenuItem value={SubscriptionStatus.READ_ONLY}>{t('subscription.readOnly')}</MenuItem>
                <MenuItem value={SubscriptionStatus.SUSPENDED}>{t('subscription.suspended')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('subscription.startsAt')}
              type="datetime-local"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              disabled={loading || saving}
            />
            <TextField
              label={t('subscription.endsAt')}
              type="datetime-local"
              value={endsAt}
              onChange={(event) => { setEndsAt(event.target.value); setDateError(''); }}
              InputLabelProps={{ shrink: true }}
              helperText={t('subscription.endsAtHelper')}
              disabled={loading || saving}
            />
            <TextField
              label={t('subscription.notes')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={2}
              disabled={loading || saving}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={loading || saving || !startsAt || !planCode.trim()}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </AppDialog>
  );
};

export default SubscriptionDialog;
